import React, { useState, useRef, useEffect } from "react";
import { 
  Send, Bot, User, Sparkles, Mail, MapPin, Briefcase, Award, 
  Linkedin, Github, ChevronRight
} from "lucide-react";
import { useLocation } from "react-router-dom";
import axios from "../api/axiosInstance";

const ChatPage = () => {
  const location = useLocation();
  const [messages, setMessages] = useState(() => {
    if (location.state?.messages) {
      return location.state.messages;
    }
    return [
      {
        sender: "bot",
        text: "Hello! I am Chahat's Assistant. I can tell you all about Chahat Jain's full-stack development experience, his certified Salesforce credentials, or help you route a direct hiring inquiry to him. What would you like to know?",
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ];
  });
  const [inputText, setInputText] = useState("");
  const [hiringFlow, setHiringFlow] = useState(() => {
    return location.state?.hiringFlow || null;
  });
  const messagesEndRef = useRef(null);

  const quickPrompts = [
    "Show Salesforce experience",
    "What is your AI expertise?",
    "Explain StockPilot architecture",
    "Are you available for remote SDE roles?",
    "I want to hire Chahat"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const callGeminiDirectAPI = async (message, history, apiKey) => {
    const systemInstruction = `You are Chahat's AI Copilot, a helpful and professional AI assistant on Chahat Jain's portfolio website. 
Your goal is to answer questions from recruiters and visitors about Chahat's technical skills, experience, projects, and availability.

Here is the context about Chahat:
- Name: Chahat Jain
- Role: Senior Full Stack SDE & Salesforce AI Automation Engineer
- Salesforce Certification: Certified Platform Developer I (PD1) and Agentforce Specialist
- Experience:
  1. Associate Software Developer at Twopir Consulting (built Apex automations, LWC, automated workflows integrating Salesforce with HubSpot & external REST APIs using n8n with over 100k+ runs, delivering a 95% reduction in manual CRM entry).
  2. IT Head & Developer at Gujarat Institute of Development Research (developed full-stack analytics dashboard, indexing, query optimization).
  3. Salesforce Developer Intern (Apex, triggers, LWC).
  4. Software Engineer Intern at NeoDocto (React, Node.js).
- Key Projects:
  1. StockPilot: Intelligent inventory SaaS built with Next.js, TypeScript, MongoDB, and Firebase. Uses Google Genkit AI models to recommend discount rules and coupons based on sales velocity. Optimized sync speeds by 35%.
  2. Google GenAI Capstone project: A career mentor chatbot using Gemini models, prompt engineering, RAG, and grounding.
- Skills:
  - Frontend: React.js, Next.js, HTML, CSS, TailwindCSS, JavaScript, TypeScript
  - Backend: Node.js, Express.js, Python (Flask), Java, C++ (300+ algorithm challenges solved)
  - Databases: MongoDB, PostgreSQL, SQLite
  - Devops/Other: Docker, Redis, Git, REST APIs, n8n, Zapier
- Education: B.Tech in Electrical Engineering with Minors in Computer Science from Nirma University.
- Availability: Currently available for remote, hybrid, or on-site software engineering (SDE), fullstack, or Salesforce developer roles.

Guidelines:
- Keep your answers concise, clear, and professional. Do not write extremely long messages.
- Highlight his strengths in fullstack development, AI/automation, and Salesforce.
- IMPORTANT FORMATTING RULE: You MUST format your response using HTML tags only. Do NOT use markdown format (such as **, *, -, etc.). Use HTML tags exclusively:
  - <strong>bold text</strong> for titles, highlights, or key metrics.
  - <ul> and <li> tags for lists and points-wise detailed breakdowns.
  - <p> for paragraphs and <br /> for line breaks.
  - <a href="mailto:chahatj06@gmail.com" style="color: #06b6d4; text-decoration: underline;">chahatj06@gmail.com</a> for links.
- If you don't know the answer, politely invite the user to contact Chahat directly using the Contact form or email (chahatj06@gmail.com).`;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite:generateContent?key=${apiKey}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            ...history.filter(m => !m.isTyping).map((msg) => ({
              role: msg.sender === "user" ? "user" : "model",
              parts: [{ text: msg.text }],
            })),
            {
              role: "user",
              parts: [{ text: message }],
            },
          ],
          systemInstruction: {
            parts: [{ text: systemInstruction }]
          }
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Gemini direct API call failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || "I'm sorry, I encountered an issue processing your request.";
  };

  const isQueryRelevant = (query) => {
    const q = query.toLowerCase().trim();
    const greetings = ['hi', 'hello', 'hey', 'yo', 'sup', 'welcome', 'hola', 'greetings', 'who are you', 'what is this', 'good morning', 'good afternoon', 'good evening'];
    if (greetings.some(word => q === word || q.startsWith(word + ' ') || q.endsWith(' ' + word))) {
      return true;
    }

    const keywords = [
      'chahat', 'jain', 'portfolio', 'resume', 'cv', 'experience', 'work', 'job', 'hire', 'hiring',
      'sde', 'developer', 'engineer', 'coding', 'programmer', 'stack', 'skills', 'contact', 'email',
      'salesforce', 'pd1', 'agentforce', 'apex', 'lwc', 'crm', 'hubspot', 'n8n', 'automation',
      'react', 'node', 'express', 'mongodb', 'postgresql', 'sqlite', 'docker', 'redis', 'next.js',
      'stockpilot', 'inventory', 'saas', 'genai', 'gemini', 'openai', 'chatbot', 'mentor', 'project',
      'twopir', 'gidr', 'neodocto', 'nirma', 'university', 'b.tech', 'education', 'degree', 'remote'
    ];

    const generalTech = [
      'javascript', 'typescript', 'python', 'java', 'c++', 'html', 'css', 'tailwind', 'api', 'rest',
      'git', 'github', 'database', 'frontend', 'backend', 'fullstack', 'web', 'app', 'application'
    ];

    return keywords.some(kw => q.includes(kw)) || generalTech.some(kw => q.includes(kw));
  };

  const isHiringQuery = (query) => {
    const q = query.toLowerCase().trim();
    const hiringWords = ['hire', 'hiring', 'job', 'position', 'role', 'interview', 'salary', 'recruit', 'recruiter', 'recruiting', 'vacancy', 'openings', 'offer', 'work with', 'looking for a developer', 'sde role', 'contract'];
    return hiringWords.some(word => q.includes(word));
  };

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      sender: "user",
      text: text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");

    // 1. Conversational Lead Collection Interceptor
    if (hiringFlow) {
      const typingMessage = {
        sender: "bot",
        text: "Thinking...",
        isTyping: true,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, typingMessage]);

      setTimeout(async () => {
        try {
          if (hiringFlow.step === "awaiting_name") {
            const name = text.trim();
            setHiringFlow({
              step: "awaiting_company",
              data: { ...hiringFlow.data, name }
            });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping
                  ? {
                      sender: "bot",
                      text: `Thanks, <strong>${name}</strong>! What is the name of <strong>your company or organization</strong>?`,
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                  : msg
              )
            );
          } else if (hiringFlow.step === "awaiting_company") {
            const company = text.trim();
            setHiringFlow({
              step: "awaiting_email",
              data: { ...hiringFlow.data, company }
            });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping
                  ? {
                      sender: "bot",
                      text: `Got it, <strong>${company}</strong>. What is <strong>your work email address</strong> so Chahat can reach you?`,
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                  : msg
              )
            );
          } else if (hiringFlow.step === "awaiting_email") {
            const email = text.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.isTyping
                    ? {
                        sender: "bot",
                        text: `That email doesn't look valid. Please provide a <strong>correct email address</strong>:`,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      }
                    : msg
                )
              );
              return;
            }

            setHiringFlow({
              step: "awaiting_message",
              data: { ...hiringFlow.data, email }
            });
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping
                  ? {
                      sender: "bot",
                      text: `Thank you. Lastly, what are the <strong>roles or project details</strong> you want to discuss?`,
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                  : msg
              )
            );
          } else if (hiringFlow.step === "awaiting_message") {
            const message = text.trim();
            const finalData = { ...hiringFlow.data, message };
            
            setMessages((prev) =>
              prev.map((msg) =>
                msg.isTyping ? { ...msg, text: "Submitting details..." } : msg
              )
            );

            try {
              const response = await axios.post('/publicRoute/lead', finalData);
              if (response.status === 200 || response.status === 201) {
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.isTyping
                      ? {
                          sender: "bot",
                          text: `<strong>Lead Captured Successfully!</strong> Thank you, <strong>${finalData.name}</strong>. Your message was delivered.<br/><br/>Chahat will follow up directly at <strong>${finalData.email}</strong>.`,
                          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        }
                      : msg
                  )
                );
              } else {
                throw new Error("Failed to submit lead");
              }
            } catch (err) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.isTyping
                    ? {
                        sender: "bot",
                        text: `Thank you, <strong>${finalData.name}</strong>! I have noted down your details. Chahat will contact you at <strong>${finalData.email}</strong> shortly.`,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      }
                    : msg
                )
              );
            } finally {
              setHiringFlow(null);
            }
          }
        } catch (error) {
          setHiringFlow(null);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.isTyping
                ? {
                    sender: "bot",
                    text: "I encountered a processing error. Let's start over! How can I help you?",
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  }
                : msg
            )
          );
        }
      }, 700);
      return;
    }

    // 2. Relevance Check
    if (!isQueryRelevant(text)) {
      const refusalMessage = {
        sender: "bot",
        text: "I am Chahat's professional assistant. I only answer questions related to Chahat Jain's experience, certifications, full-stack projects, and job inquiries. Please try asking about Chahat's technical background or hiring availability!",
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setTimeout(() => {
        setMessages((prev) => [...prev, refusalMessage]);
      }, 400);
      return;
    }

    // 3. Add thinking indicator
    const typingMessage = {
      sender: "bot",
      text: "Thinking...",
      isTyping: true,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages((prev) => [...prev, typingMessage]);

    try {
      let botResponseText = "";
      const clientApiKey = process.env.REACT_APP_GEMINI_API_KEY;
      let success = false;

      // Mode 1: Client-Side direct call
      if (clientApiKey) {
        try {
          botResponseText = await callGeminiDirectAPI(text, messages, clientApiKey);
          success = true;
        } catch (directError) {
          console.warn("Direct API call failed, trying backend proxy...", directError);
        }
      }

      // Mode 2: Backend Proxy call
      if (!success) {
        try {
          const response = await axios.post('/publicRoute/chat', {
            message: text,
            history: messages.filter(m => !m.isTyping),
          });
          if (response.status === 200 && response.data?.response) {
            botResponseText = response.data.response;
            success = true;
          }
        } catch (backendError) {
          console.warn("Backend proxy failed, using local fallback responses...", backendError);
        }
      }

      // Mode 3: Local Fallback responses
      if (!success) {
        const q = text.toLowerCase();
        if (q.includes("salesforce") || q.includes("apex") || q.includes("lwc") || q.includes("crm")) {
          botResponseText = "Chahat is a certified Salesforce Platform Developer I (PD1) and Agentforce Specialist with 2+ years of experience. At Twopir Consulting, he built Apex triggers, LWC features, and automated hubspot integrations, reducing manual data entry by 95%.";
        } else if (q.includes("ai") || q.includes("automation") || q.includes("n8n") || q.includes("gemini")) {
          botResponseText = "Chahat specializes in AI Engineering & Workflow Automation. He has built qualification chatbots using OpenAI and n8n (100k+ workflow runs). He also holds a Google GenAI Capstone certification for his Gemini career mentor project implementing RAG.";
        } else if (q.includes("stockpilot") || q.includes("inventory")) {
          botResponseText = "StockPilot is an intelligent inventory SaaS built with Next.js, TypeScript, MongoDB, and Firebase. It uses Google Genkit AI models to recommend discount rules based on sales velocity, reducing manual updates and improving inventory sync speeds by 35%.";
        } else if (q.includes("hire") || q.includes("job") || q.includes("available")) {
          botResponseText = "Chahat is currently open for Full-Time software engineering (SDE), Full-Stack, or Salesforce developer roles. He is open to Remote, Hybrid, or On-site positions and holds a B.Tech in Electrical Engineering with Computer Science minors from Nirma University.";
        } else if (q.includes("chahat") || q.includes("about") || q.includes("who is") || q.includes("tell me more")) {
          botResponseText = "Chahat Jain is a Full-Stack Software Developer & Salesforce AI Automation Engineer with over 2 years of professional coding experience. He is a certified Salesforce Platform Developer I (PD1) and Agentforce Specialist. Chahat specializes in building MERN/Next.js applications, custom AI integrations, and CRM workflow automation. Would you like to know more about his projects, experience, or certifications?";
        } else if (q.includes("hello") || q.includes("hi") || q.includes("hey") || q.includes("welcome")) {
          botResponseText = "Hello! I can tell you about Chahat's software experience, full-stack web development, Salesforce automation, or AI projects. How can I help you today?";
        } else {
          botResponseText = "I'm Chahat's Assistant. I can tell you about his 2+ years of coding, fullstack React/Node projects, and Salesforce automation. Feel free to click one of the quick prompts!";
        }
      }

      // Replace typing indicator
      setMessages((prev) =>
        prev.map((msg) =>
          msg.isTyping
            ? {
                sender: "bot",
                text: botResponseText,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }
            : msg
        )
      );

      // Conversational Lead flow trigger
      if (isHiringQuery(text)) {
        setHiringFlow({ step: "awaiting_name", data: {} });
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              text: "I would be glad to connect you with Chahat regarding hiring opportunities! To start the process, could you please tell me <strong>your name</strong>?",
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }
          ]);
        }, 800);
      }

    } catch (err) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.isTyping
            ? {
                sender: "bot",
                text: "I encountered an error. Please try again in a moment.",
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }
            : msg
        )
      );
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white flex flex-col items-center justify-start">
      {/* Cyberpunk ambient glowing backdrops */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10">
        
        {/* Left Side: Chahat's Profile Dashboard CARD */}
        <div 
          className="lg:col-span-4 bg-slate-950/70 border border-white/10 p-6 rounded-2xl backdrop-blur-xl flex flex-col justify-between shadow-2xl"
          style={{ boxShadow: "0 0 40px rgba(0, 0, 0, 0.4)" }}
        >
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-indigo-500 p-0.5 shadow-lg">
                  <div className="w-full h-full rounded-[14px] bg-slate-900 flex items-center justify-center font-bold text-xl text-white">
                    CJ
                  </div>
                </div>
                <span className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 border-2 border-slate-950 rounded-full animate-pulse" />
              </div>
              <div>
                <h2 className="font-extrabold text-lg text-white tracking-wide">Chahat Jain</h2>
                <p className="text-xs text-cyan-400 font-medium">AI & Salesforce Engineer</p>
              </div>
            </div>

            <p className="text-xs text-gray-400 leading-relaxed mb-6">
              Hi, I am Chahat's Assistant. Ask me about his projects, integrations, Salesforce configurations, or leave a message to connect.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 text-xs text-gray-300">
                <Briefcase className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Twopir Consulting (Associate SDE)</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-300">
                <Award className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Salesforce Certified PD1</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-300">
                <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>Gujarat, India (Open to Remote)</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-300">
                <Mail className="w-4 h-4 text-cyan-400 shrink-0" />
                <a href="mailto:chahatj06@gmail.com" className="hover:underline hover:text-white">chahatj06@gmail.com</a>
              </div>
            </div>

            <div className="border-t border-white/5 pt-6">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Key Focus Areas</h4>
              <div className="flex flex-wrap gap-2">
                <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-gray-300 font-medium">Agentforce</span>
                <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-gray-300 font-medium">Apex / LWC</span>
                <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-gray-300 font-medium">n8n Automation</span>
                <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-gray-300 font-medium">Next.js / Node.js</span>
                <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-[10px] text-gray-300 font-medium">RAG & Prompt Eng.</span>
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-8 pt-6 border-t border-white/5 justify-center">
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="p-2.5 bg-white/5 border border-white/10 hover:border-cyan-400/50 hover:bg-cyan-500/10 rounded-xl text-gray-400 hover:text-cyan-400 transition-all">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href="https://github.com" target="_blank" rel="noreferrer" className="p-2.5 bg-white/5 border border-white/10 hover:border-cyan-400/50 hover:bg-cyan-500/10 rounded-xl text-gray-400 hover:text-cyan-400 transition-all">
              <Github className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Right Side: Immersive Chat Workspace */}
        <div 
          className="lg:col-span-8 h-[550px] md:h-[650px] bg-slate-950/70 border border-white/10 rounded-2xl backdrop-blur-xl flex flex-col overflow-hidden shadow-2xl"
          style={{ boxShadow: "0 0 40px rgba(0, 0, 0, 0.4)" }}
        >
          {/* Chat Window Header */}
          <div className="px-6 py-4 border-b border-white/10 bg-gradient-to-r from-cyan-900/20 via-blue-900/20 to-indigo-900/20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-500/10 border border-cyan-400/20 rounded-xl">
                <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-sm tracking-wide text-white">Chahat's AI Copilot</span>
                  <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-400/20 text-[9px] text-cyan-400 font-semibold uppercase">
                    Assistant
                  </span>
                </div>
                <span className="text-[10px] text-gray-400 flex items-center gap-1.5 mt-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block animate-ping" />
                  Ask anything about skills, certifications, or hiring
                </span>
              </div>
            </div>
          </div>

          {/* Chat Bubble Messages Container */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-950/30">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-4 ${msg.sender === "user" ? "flex-row-reverse" : ""}`}
              >
                <div
                  className={`p-2.5 rounded-xl border shrink-0 flex items-center justify-center ${
                    msg.sender === "user"
                      ? "bg-indigo-500/20 border-indigo-400/20 text-indigo-400"
                      : "bg-cyan-500/20 border-cyan-400/20 text-cyan-400"
                  }`}
                >
                  {msg.sender === "user" ? <User className="w-4.5 h-4.5" /> : <Bot className="w-4.5 h-4.5" />}
                </div>
                <div className="max-w-[80%] flex flex-col">
                  <div
                    className={`px-4.5 py-3 rounded-2xl text-xs leading-relaxed font-medium ${
                      msg.sender === "user"
                        ? "bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-tr-none shadow-lg"
                        : "bg-white/5 border border-white/10 text-gray-200 rounded-tl-none"
                    }`}
                  >
                    {msg.sender === "bot" ? (
                      <div className="space-y-2" dangerouslySetInnerHTML={{ __html: msg.text }} />
                    ) : (
                      msg.text
                    )}
                  </div>
                  <span className={`text-[9px] text-gray-500 mt-1.5 ${msg.sender === "user" ? "text-right" : ""}`}>
                    {msg.time}
                  </span>
                </div>
              </div>
            ))}
             <div ref={messagesEndRef} className="h-4" />
          </div>

          {/* Suggestion Prompts Section */}
          <div className="px-6 py-3 border-t border-white/5 bg-slate-950/60 flex flex-wrap gap-2 justify-start">
            {quickPrompts.map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => handleSendMessage(prompt)}
                className="text-[10px] font-semibold text-cyan-300 hover:text-white bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-400/20 px-3 py-1.5 rounded-lg transition-all text-left cursor-pointer flex items-center gap-1 group"
              >
                <span>{prompt}</span>
                <ChevronRight className="w-3 h-3 text-cyan-400/50 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ))}
          </div>

          {/* Form Input Area */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage(inputText);
            }}
            className="p-4 border-t border-white/10 bg-slate-950 flex gap-3"
          >
            <input
              type="text"
              placeholder="Ask about Salesforce PD1, stockpilot, n8n, availability..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-5 py-3 text-xs text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
            />
            <button
              type="submit"
              className="px-5 bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 text-slate-950 font-bold rounded-xl transition cursor-pointer flex items-center justify-center shadow-lg"
            >
              <Send className="w-4 h-4 text-slate-950 mr-1.5" />
              <span className="text-xs">Send</span>
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export default ChatPage;
